import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import FinanceRegisterModel from './models/FinanceRegisterModel'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues, formatDate } from '../../shared/CommonFunctions'
import FileList from '../../shared/file-upload/FileList'
import FinanceDetails from '../../shared/finance/FinanceDetails'
import { generateFinanceRegisterPdf } from './utils/financeRegisterPdf'

function ViewFinanceRegister({ FinanceRegisterData, onCancel = () => {} }) {
  const [financeRegisterData, setFinanceRegisterData] = useState(new FinanceRegisterModel())
  const [fileTitles, setFileTitles] = useState([])
  const [customerFiles, setCustomerFiles] = useState([])
  const [financePartners, setFinancePartners] = useState([])
  const [financePartnersError, setFinancePartnersError] = useState('')
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null)

  useEffect(() => {
    setFinanceRegisterData(FinanceRegisterData || new FinanceRegisterModel())
  }, [FinanceRegisterData])

  useEffect(() => {
    const key = FinanceRegisterData?.FinancePartnerId ?? FinanceRegisterData?.FinancePartnerName ?? null
    setSelectedFinancePartnerId(key)
  }, [FinanceRegisterData?.FinancePartnerId, FinanceRegisterData?.FinancePartnerName])

  useEffect(() => {
    const fetchFinancePartners = async () => {
      try {
        setFinancePartnersError('')
        const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
          params: { UserId: getStorageValues('userId') }
        })
        const payload = response?.data?.data
        const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
        setFinancePartners(rows)
      } catch (error) {
        setFinancePartners([])
        setFinancePartnersError('Unable to load finance partners.')
      }
    }
    fetchFinancePartners()
  }, [])

  useEffect(() => {
    const customerId = FinanceRegisterData?.CustomerId
    if (!customerId) {
      setFileTitles([])
      return
    }
    const fetchFiles = async () => {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/file/list`, {
          entityId: customerId,
          entityType: 'customer'
        })
        const payload = response?.data?.data
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : []
        setCustomerFiles(normalized)
        const titles = normalized
          .map((file) => file.FileTitle || '')
          .filter((title) => title && title.trim().length > 0)
        setFileTitles(titles)
      } catch (error) {
        console.log(error)
        setFileTitles([])
      }
    }
    fetchFiles()
  }, [FinanceRegisterData?.CustomerId])

  const handlePreviewDocument = useCallback((file) => {
    if (!file || !file.FileLibraryId) return
    const url = `${getApiBaseUrl()}/file/download/${file.FileLibraryId}?inline=1`
    window.open(url, '_blank')
  }, [])

  const residenceAddress = useMemo(() => {
    return [
      financeRegisterData.CustomerAddressLine1,
      financeRegisterData.CustomerAddressLine2
    ].filter(Boolean).join(', ')
  }, [financeRegisterData.CustomerAddressLine1, financeRegisterData.CustomerAddressLine2])

  const residenceCityState = useMemo(() => {
    return [
      financeRegisterData.CustomerCity,
      financeRegisterData.CustomerState
    ].filter(Boolean).join(', ')
  }, [financeRegisterData.CustomerCity, financeRegisterData.CustomerState])

  const officeAddress = useMemo(() => {
    return [
      financeRegisterData.OfficeAddressLine1,
      financeRegisterData.OfficeAddressLine2
    ].filter(Boolean).join(', ')
  }, [financeRegisterData.OfficeAddressLine1, financeRegisterData.OfficeAddressLine2])

  const officeCityState = useMemo(() => {
    return [
      financeRegisterData.OfficeCity,
      financeRegisterData.OfficeState
    ].filter(Boolean).join(', ')
  }, [financeRegisterData.OfficeCity, financeRegisterData.OfficeState])

  const handlePreviewPdf = useCallback(async () => {
    const { dataUri, base64 } = await generateFinanceRegisterPdf(financeRegisterData, fileTitles)
    const dataPart = base64 || dataUri.split(',')[1] || ''
    const byteCharacters = atob(dataPart)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })
    const blobUrl = URL.createObjectURL(blob)
    const previewWindow = window.open('', '_blank')
    if (previewWindow) {
      previewWindow.document.title = 'Finance Register PDF'
      previewWindow.document.body.style.margin = '0'
      previewWindow.document.body.innerHTML = `

        <iframe
          src="${blobUrl}"
          style="border:0; width:100%; height:100vh;"
          title="Finance Register PDF"
        ></iframe>
      `
    } else {
      window.open(blobUrl, '_blank')
    }
  }, [financeRegisterData, fileTitles])

  const interestRateOptions = useMemo(() => {
    if (!financeRegisterData.InterestRate) return []
    const valueStr = financeRegisterData.InterestRate.toString()
    return [{ value: valueStr, label: valueStr, id: 'current-rate' }]
  }, [financeRegisterData.InterestRate])

  const selectedInterestRate = useMemo(() => {
    if (!financeRegisterData.InterestRate) return null
    const valueStr = financeRegisterData.InterestRate.toString()
    return interestRateOptions.find((opt) => opt.value === valueStr) || null
  }, [financeRegisterData.InterestRate, interestRateOptions])

  const noop = () => {}

  return (
    <>
      <div className="row">
        <div className="col-lg-12 col-sm-12 col-12 mb-3">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Customer Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Customer Name</label>
                <input type="text" className="form-control" value={financeRegisterData.CustomerName || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Email Address</label>
                <input type="text" className="form-control" value={financeRegisterData.EmailId || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Phone Number</label>
                <input type="text" className="form-control" value={financeRegisterData.MobileNo || ''} disabled />
              </div>
              <div className="col-lg-12 col-sm-12 col-12 mb-3">
                <label className="form-label">Address</label>
                <input type="text" className="form-control" value={financeRegisterData.CustomerAddress || ''} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Documents</h6>
            <FileList
              files={customerFiles}
              onDownload={handlePreviewDocument}
              showRemove={false}
              showDownload={true}
              downloadLabel="Preview"
            />
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Vehicle Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Sales #</label>
                <input type="text" className="form-control" value={financeRegisterData.SalesNumber || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle</label>
                <input type="text" className="form-control" value={financeRegisterData.VehicleLabel || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle Cost</label>
                <input type="text" className="form-control" value={financeRegisterData.VehicleCost || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Chassis No</label>
                <input type="text" className="form-control" value={financeRegisterData.ChassisNo || ''} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Engine No</label>
                <input type="text" className="form-control" value={financeRegisterData.EngineNo || ''} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <FinanceDetails
            financePartners={financePartners}
            selectedFinancePartnerId={selectedFinancePartnerId}
            financePartnersError={financePartnersError}
            onFinancePartnerClick={noop}
            downPaymentValue={financeRegisterData.DownPayment || ''}
            emiTenureValue={financeRegisterData.EMITenure || ''}
            processingFeesValue={financeRegisterData.ProcessingFees || ''}
            interestRateValue={financeRegisterData.InterestRate || ''}
            emiAmountValue={financeRegisterData.EMIAmount || ''}
            onDownPaymentChange={noop}
            onEmiTenureChange={noop}
            onProcessingFeesChange={noop}
            onInterestRateChange={noop}
            onEmiAmountChange={noop}
            interestRateOptions={interestRateOptions}
            interestRateLoading={false}
            selectedInterestRate={selectedInterestRate}
            onCalculateEmi={noop}
            loanSummaryError={''}
            disabled={true}
          />
        </div>

        <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <label className="form-label">Loan Application No</label>
          <input type="text" className="form-control" value={financeRegisterData.LoanApplicationNo || ''} disabled />
        </div>
        <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <label className="form-label">Status</label>
          <input type="text" className="form-control" value={financeRegisterData.Status || ''} disabled />
        </div>
        <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <label className="form-label">Loan Account Number</label>
          <input type="text" className="form-control" value={financeRegisterData.LoanAccountNumber || ''} disabled />
        </div>
        <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <label className="form-label">EMI Start Date</label>
          <input type="text" className="form-control" value={formatDate(financeRegisterData.NextEmiDate)} disabled />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button type="button" className="btn btn-outline-primary" onClick={handlePreviewPdf}>Print</button>
        <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </>
  )
}

export default ViewFinanceRegister